import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";

const EvaluationsChart = ({ data = {}, permits = {}, appliedFilters = {}, assets = [] }) => {
  const navigate = useNavigate();

  const canShowData =
    permits.isSuperuser || (permits.hasAnyRole && permits.canEvaluationRead);

  const leftFixed = ["total_pending", "total_finished"];
  const rightFixed = ["definited", "designed", "implemented", "validated", "documented"];

  const labelMap = {
    total_pending: "Pendiente",
    total_finished: "Evaluado",
    definited: "Definido",
    designed: "Diseñado",
    implemented: "Implementado",
    validated: "Validado",
    documented: "Documentado",
  };

  // Función para generar series individuales para toggle por leyenda
  const generateSeries = (keys) =>
    keys.map((key) => ({
      name: labelMap[key],
      data: [
        canShowData
          ? data[key] || 0
          : Math.floor(Math.random() * 51),
      ],
    }));

  const leftSeries = useMemo(() => generateSeries(leftFixed), [data, canShowData]);
  const rightSeries = useMemo(() => generateSeries(rightFixed), [data, canShowData]);

  const colorsLeft = canShowData ? ["#9B59B6", "#2ECC71"] : ["#CCCCCC", "#AAAAAA"];
  const colorsRight = canShowData
    ? ["#E74C3C", "#3498DB", "#F39C12", "#2ECC71", "#9B59B6"]
    : ["#CCCCCC", "#AAAAAA", "#999999", "#888888", "#777777"];

  // ✅ Función helper para construir URL con AMBOS filtros del Dashboard
  const buildNavigationUrl = (additionalParams = {}) => {
    const params = new URLSearchParams();
    
    // Agregar parámetros específicos del gráfico (status o stage)
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value) params.append(key, value); // ✅ SIN encodeURIComponent
    });
    
    // ✅ Agregar filtros de ACTIVOS del Dashboard
    if (appliedFilters.asset && appliedFilters.asset.length > 0) {
      const assetNames = appliedFilters.asset
        .map(code => assets.find(a => a.code === code)?.name)
        .filter(Boolean);
      
      if (assetNames.length > 0) {
        params.append('assets', assetNames.join(','));
      }
    }
    
    // ✅ Agregar filtros de ÁREAS del Dashboard
    if (appliedFilters.area && appliedFilters.area.length > 0) {
      const allFilterAreas = assets.reduce((acc, asset) => {
        if (asset.filter_areas) {
          asset.filter_areas.forEach(area => {
            if (!acc.find(a => a.id === area.id)) {
              acc.push(area);
            }
          });
        }
        return acc;
      }, []);

      const areaNames = appliedFilters.area
        .map(id => allFilterAreas.find(a => a.id === id)?.name)
        .filter(Boolean);
      
      if (areaNames.length > 0) {
        params.append('areas', areaNames.join(','));
      }
    }
    
    const queryString = params.toString();
    return `/evaluation${queryString ? `?${queryString}` : ''}`;
  };

  const leftOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (!canShowData) return;
            const label = labelMap[leftFixed[config.seriesIndex]];
            navigate(buildNavigationUrl({ status: label })); // ✅ Navega con ambos filtros
          },
        },
      },
      plotOptions: { bar: { horizontal: false, columnWidth: "70%", borderRadius: 6 } },
      xaxis: { categories: ["Estado"], labels: { show: false } },
      legend: { show: true, onItemClick: { toggleDataSeries: true } },
      colors: colorsLeft,
      dataLabels: { enabled: true },
      tooltip: { y: { formatter: (val) => val ?? 0 } },
    }),
    [canShowData, colorsLeft, navigate, appliedFilters, assets] // ✅ Dependencias actualizadas
  );

  const rightOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (!canShowData) return;
            const label = labelMap[rightFixed[config.seriesIndex]];
            navigate(buildNavigationUrl({ stage: label })); // ✅ Navega con ambos filtros
          },
        },
      },
      plotOptions: { bar: { horizontal: false, columnWidth: "70%", borderRadius: 6 } },
      xaxis: { categories: ["Estado"], labels: { show: false } },
      legend: { show: true, onItemClick: { toggleDataSeries: true } },
      colors: colorsRight,
      dataLabels: { enabled: true },
      tooltip: { y: { formatter: (val) => val ?? 0 } },
    }),
    [canShowData, colorsRight, navigate, appliedFilters, assets] // ✅ Dependencias actualizadas
  );

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: "40px",
        cursor: canShowData ? "default" : "not-allowed",
        position: "relative",
      }}
    >
      <div style={{ flex: 1 }}>
        <Chart options={leftOptions} series={leftSeries} type="bar" height={300} />
      </div>
      <div style={{ flex: 2 }}>
        <Chart options={rightOptions} series={rightSeries} type="bar" height={300} />
      </div>

      {!canShowData && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            fontWeight: "bold",
            color: "#555",
          }}
        >
          No tienes permisos para ver este gráfico
        </div>
      )}
    </div>
  );
};

export default EvaluationsChart;